import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { CgFileRemove } from 'react-icons/cg';
import { IoDocumentTextOutline } from 'react-icons/io5';

import { getServiceId } from '@/api/tokens.helper.ts';
import { DateTimePicker, Divider, LoaderSpinner } from '@/components';
import { Button, ButtonOutlined } from '@/components/Button';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal';
import { DocumentPreviewModal } from '@/components/DocumentPreview/DocumentPreview';
import { Autocomplete, Checkbox, Input, TextArea } from '@/components/Inputs';
import { StatusPill } from '@/components/StatusPill/StatusPill';
import { useTaskOptions } from '@/hooks/useTaskOptions';
import { useAuth } from '@/store/auth/auth.store.ts';
import {
  useDocumentByIdQuery,
  useUploadDocumentsFileMutation,
} from '@/store/documents/documents.store.ts';
import { useAllEquipmentQuery } from '@/store/equipment/equipment.store';
import type {
  CreateInvoicePayload,
  Invoice,
  InvoiceNote,
} from '@/store/expenses-store/invoices/invoices.dto.ts';
import { useInvoice } from '@/store/expenses-store/invoices/invoices.store.ts';
import { useTransactionsStore } from '@/store/expenses-store/transactions/transactions.store.ts';
import { getServiceProviders } from '@/store/service-providers/service-providers.api.ts';
import {
  useServiceProvidersQuery,
  useSingleServiceProviderQuery,
} from '@/store/service-providers/service-providers.store.ts';
import type { TaskResponse } from '@/store/tasks/tasks.dto.ts';
import { useTasks } from '@/store/tasks/tasks.store.ts';
import { compressImage } from '@/utils/compressImage';
import { extractAI } from '@/utils/extractAI.ts';
import { formatAmountDisplay, moneyInputProps, sanitizeAmount, toAmountString } from '@/utils/form';
import { OWNER_GROUP_ROLES } from '@/utils/roles';
import { toast } from '@/utils/toast.ts';
import { useDebounce } from '@/utils/use-debounce';
import { useRoles } from '@hooks/useRoles';
import { ExpenseEquipmentField } from '@pages/Expenses/components/ExpenseEquipmentField.tsx';
import {
  formatRecipientContact,
  invoiceRecipientContact,
  isCustomRecipient,
} from '@pages/Expenses/InvoicesPage/utils/customRecipient';
import {
  invoiceStatusLabel,
  invoiceStatusTone,
  isInvoicePaid,
} from '@pages/Expenses/InvoicesPage/utils/invoiceStatus';
import { useInvoicePayment } from '@pages/Expenses/InvoicesPage/utils/useInvoicePayment';

import styles from './InvoiceModal.module.scss';
import { ModalOrPanel } from './ModalOrPanel';
import viewStyles from './TransactionModal.module.scss';

type InvoiceModalProps = {
  open: boolean;
  onClose: () => void;
  invoice?: Invoice | null;
  documentName?: string;
  taskId?: string;
  initialDocumentId?: string;
  initialDocumentFileName?: string;
  serviceProviderId?: string;
  counterpartName?: string;
  isExist?: boolean;
  onSuccess?: () => void;
  asPanel?: boolean;
};

type ExistingInvoiceProps = {
  invoice?: Invoice | null;
  tasks: TaskResponse[];
  equipmentList: { id: string; name: string }[];
  documentName?: string;
  counterpartName?: string;
  onPreviewDocument: (doc: { contentType: string; name: string; storageLink: string }) => void;
};

/**
 * Rows in the service-provider dropdown. The search is the service's, so this is one
 * screenful of matches rather than a slab of the vessel's whole roster to sift locally.
 */
const MERCHANT_OPTIONS_LIMIT = 20;

const InvoiceModal = ({
  open,
  onClose,
  invoice,
  documentName,
  taskId,
  initialDocumentId,
  initialDocumentFileName,
  serviceProviderId,
  counterpartName,
  isExist,
  onSuccess,
  asPanel,
}: InvoiceModalProps) => {
  const { createInvoice, updateInvoice, removeInvoice, loading } = useInvoice();
  const { currentTask, getTaskById } = useTasks();
  // Local, and only once the modal is open: this component is mounted closed behind the
  // task modal and the expenses lists, and the old `getAllTasks()` here replaced the
  // shared task list with every task on the vessel the moment any of them rendered.
  const taskOptions = useTaskOptions(open);
  const transactionCategories = useTransactionsStore((state) => state.transactionCategories);
  const getTransactionsCategories = useTransactionsStore(
    (state) => state.getTransactionsCategories,
  );
  const { data: equipmentList = [] } = useAllEquipmentQuery(open);
  const vesselId = getServiceId();
  const { mutateAsync: uploadDocumentsFile } = useUploadDocumentsFileMutation();
  const session = useAuth((state) => state.session);
  // Gated on `open` for the same reason the equipment and category fetches above are:
  // this component is mounted closed behind the task card and the expenses lists, so an
  // ungated query here asked for a hundred service providers every time a task was
  // opened — none of which the closed modal renders.
  //
  // The search is the service's too. This used to fetch a hundred rows with an empty
  // search and then narrow them with `.filter()` as you typed, which is a page-sized
  // guess at a set the service can cut exactly, and silently wrong on a vessel with a
  // hundred-and-first provider.
  const [merchantSearch, setMerchantSearch] = useState('');
  const debouncedMerchantSearch = useDebounce(merchantSearch);
  const spQuery = useServiceProvidersQuery(
    MERCHANT_OPTIONS_LIMIT,
    { vesselId, search: debouncedMerchantSearch },
    { enabled: open },
  );
  const serviceProviders = spQuery.data?.pages.flatMap((p) => p.items) ?? [];
  const hasExistingInvoice = isExist ?? !!invoice;
  const { canPay, payInvoice, payingId } = useInvoicePayment();
  const [isEditing, setIsEditing] = useState(false);
  const [needApproval, setNeedApproval] = useState(false);
  const { hasAnyRole } = useRoles();
  const isOwner = hasAnyRole(OWNER_GROUP_ROLES);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isTemporarilyClosed, setIsTemporarilyClosed] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<{
    contentType: string;
    name: string;
    storageLink: string;
  } | null>(null);
  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleDeleteClick = () => {
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (invoice?.id) {
      await removeInvoice(invoice.id);
      handleClose();
      onSuccess?.();
    }
  };

  const handleClose = () => {
    setIsEditing(false);
    onClose();
  };

  const handleOpenPreview = (doc: { contentType: string; name: string; storageLink: string }) => {
    setIsTemporarilyClosed(true);
    setPreviewDocument(doc);
  };

  const handleClosePreview = () => {
    setPreviewDocument(null);
    setIsTemporarilyClosed(false);
  };

  const showForm = !hasExistingInvoice || isEditing;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [date, setDate] = useState<Date | null>(null);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [merchantId, setMerchantId] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [equipmentId, setEquipmentId] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [notes, setNotes] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [documentFileName, setDocumentFileName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [extractFile, setExtractFile] = useState<File | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  // const [isDocumentRemoved, setIsDocumentRemoved] = useState(false);
  const [merchantError, setMerchantError] = useState('');
  const [counterpartHint, setCounterpartHint] = useState<string | undefined>(counterpartName);
  /** The recipient's name when it was typed rather than picked; '' while a provider is set. */
  const [customName, setCustomName] = useState('');
  const [customEmail, setCustomEmail] = useState('');
  const [customPhone, setCustomPhone] = useState('');
  // Opened from a provider's own context, the recipient is fixed — nothing else to enter.
  const canChooseRecipient = !serviceProviderId;

  // Read by id, not picked out of the options: once the options are a search result the
  // chosen provider is often not among them, and this one feeds the saved payload.
  const merchant = useSingleServiceProviderQuery({ id: merchantId, vesselId }).data ?? null;
  const equipment = equipmentList.find((option) => option.id === equipmentId);

  useEffect(() => {
    if (!open) return;
    void getTransactionsCategories();
  }, [open, getTransactionsCategories]);

  useEffect(() => {
    if (taskId) void getTaskById(taskId as string);
  }, [getTaskById, taskId]);

  useEffect(() => {
    if (!open) return;
    if (!invoice) {
      setInvoiceNumber('');
      setDate(null);
      setDueDate(null);
      setMerchantId(serviceProviderId ?? '');
      setMerchantSearch('');
      setAmount('');
      setCurrency('USD');
      setEquipmentId('');
      setSelectedTaskId(taskId ?? '');
      setTaskSearch('');
      setCategoryId('');
      setCategorySearch('');
      setNotes('');
      setDocumentId(initialDocumentId ?? '');
      setDocumentFileName(initialDocumentFileName ?? '');
      setExtractFile(null);
      // setIsDocumentRemoved(false);
      setMerchantError('');
      setNeedApproval(false);
      setCustomName('');
      setCustomEmail('');
      setCustomPhone('');
      return;
    }

    const contact = invoiceRecipientContact(invoice);
    setCustomName(isCustomRecipient(invoice) ? (invoice.company_name ?? '') : '');
    setCustomEmail(contact.email ?? '');
    setCustomPhone(contact.phone ?? '');

    setInvoiceNumber(invoice.invoice_number ?? '');
    setDate(invoice.date ? new Date(invoice.date) : null);
    setDueDate(invoice.detail?.dueDate ? new Date(invoice.detail.dueDate as string) : null);
    setMerchantId(invoice.service_provider_id ?? '');
    setMerchantSearch('');
    setAmount(sanitizeAmount(String(invoice.amount?.number ?? '')));
    setCurrency(invoice.amount?.currency ?? 'USD');
    setEquipmentId(invoice.equipments?.[0]?.id ?? '');
    setSelectedTaskId(invoice.task_id ?? '');
    setTaskSearch('');
    setCategoryId((invoice.detail?.category as string) ?? '');
    setCategorySearch('');
    setNotes(Array.isArray(invoice.notes) ? invoice.notes.map((n) => n.note).join('\n') : '');
    setDocumentId(invoice.document_id ?? '');
    setDocumentFileName(documentName ?? '');
    setExtractFile(null);
    // setIsDocumentRemoved(false);
    setMerchantError('');
  }, [
    documentName,
    initialDocumentFileName,
    initialDocumentId,
    invoice,
    open,
    serviceProviderId,
    taskId,
  ]);

  // Set equipment from task once when it loads (doesn't overwrite manual selection)
  const [taskEquipmentApplied, setTaskEquipmentApplied] = useState(false);

  useEffect(() => {
    if (!open) {
      setTaskEquipmentApplied(false);
      return;
    }
    if (taskEquipmentApplied || !currentTask?.equipment?.id) return;
    setEquipmentId(String(currentTask.equipment.id));
    setTaskEquipmentApplied(true);
  }, [open, currentTask, taskEquipmentApplied]);

  useEffect(() => {
    if (open) setCounterpartHint(counterpartName);
  }, [open, counterpartName]);

  const addDays = (value: Date | null, days: number): Date | null => {
    if (!value) return null;
    const dateValue = new Date(value);
    if (Number.isNaN(dateValue.getTime())) return null;
    dateValue.setDate(dateValue.getDate() + days);
    return dateValue;
  };

  const minDueDate = date ? addDays(date, 1) || undefined : undefined;
  const maxDate = dueDate ? addDays(dueDate, -1) || undefined : undefined;

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const rawFile = event.target.files?.[0];
    event.target.value = '';
    if (!rawFile) return;
    setIsUploading(true);
    try {
      const file = await compressImage(rawFile);

      const response = await uploadDocumentsFile({ file, parentId: null });
      const responseData = response?.data as { id?: string } | { id?: string }[] | undefined;
      const uploadedId = Array.isArray(responseData) ? responseData[0]?.id : responseData?.id;
      setDocumentId(uploadedId ?? '');
      setDocumentFileName(file.name);
      setExtractFile(file);
      // setIsDocumentRemoved(false);
    } catch (err) {
      console.error('Invoice upload error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleExtract = async () => {
    if (!extractFile) return;
    setIsExtracting(true);
    try {
      const formData = new FormData();
      formData.append('file', extractFile);
      formData.append('extractor_name', 'invoice');
      formData.append('vessel_id', vesselId);

      const result = await extractAI({ formData, type: 'invoice', method: 'extract' });

      if (result) {
        if (result.data.result.invoice_number) setInvoiceNumber(result.data.result.invoice_number);
        if (result.data.result.date) setDate(new Date(result.data.result.date));
        if (result.data.result.due_date) setDueDate(new Date(result.data.result.due_date));
        if (result.data.result.total_amount)
          setAmount(sanitizeAmount(String(result.data.result.total_amount)));
        if (result.data.result.currency) setCurrency(result.data.result.currency);
        if (result.data.result.description) setNotes(result.data.result.description);
        if (result.data.result.service_provider) {
          // Asked of the service by name rather than matched against whatever rows the
          // dropdown happens to be showing — the scanned name is rarely the one typed
          // into the search box.
          const scannedName = result.data.result.service_provider as string;
          const { items } = await getServiceProviders(MERCHANT_OPTIONS_LIMIT, 1, {
            vesselId,
            search: scannedName,
          });
          const sp = items.find((p) => p.name.toLowerCase() === scannedName.toLowerCase());
          if (sp) {
            setMerchantId(sp.id);
            setCustomName('');
          } else if (canChooseRecipient) {
            // Not one of the vessel's providers — keep the scanned name as a custom recipient
            // instead of dropping it.
            setMerchantId('');
            setCustomName(scannedName.trim());
          }
        }
        if (result.data.related_equipment) {
          const eq = equipmentList.find(
            (e) => e.name.toLowerCase() === result.data.result.related_equipment.toLowerCase(),
          );
          if (eq) setEquipmentId(eq.id);
        }
        toast.success('Invoice extracted');
        setExtractFile(null);
      }
    } catch (err) {
      console.error('Invoice extract error:', err);
    } finally {
      setIsExtracting(false);
    }
  };

  const toIsoDate = (value: Date | null) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toISOString();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const effectiveMerchantId = serviceProviderId ?? merchant?.id ?? '';
    // A name left typed in the picker counts as entered, as it does in the transaction form.
    const recipientName = (customName || merchantSearch.trim()).slice(0, 500);
    const isCustom = canChooseRecipient && !effectiveMerchantId && !!recipientName;
    if (!effectiveMerchantId && !isCustom && !counterpartName) {
      setMerchantError('Pick a service provider or enter the recipient name');
      return;
    }
    const creatorId = session?.id ?? '';
    const notesArray: InvoiceNote[] = notes
      ? notes
          .split('\n')
          .filter(Boolean)
          .map((text) => ({
            creator_id: creatorId,
            note: text,
            date: new Date().toISOString(),
          }))
      : [];
    const payload: CreateInvoicePayload = {
      invoice_number: invoiceNumber,
      amount: {
        number: toAmountString(amount),
        currency: currency === '$' ? 'USD' : currency,
      },
      equipments: equipment ? [{ id: equipment.id, name: equipment.name }] : [],
      // Left out, not '', for a custom recipient: wallet validates a present id as a uuid. Set
      // to undefined explicitly so it also overrides the old provider when an edit switches.
      service_provider_id: isCustom ? undefined : effectiveMerchantId,
      company_name: isCustom ? recipientName : (merchant?.name ?? counterpartName ?? ''),
      attachments: documentId ? [documentId] : [],
      detail: {
        dueDate: toIsoDate(dueDate),
        category: categoryId || undefined,
        recipient:
          isCustom && (customEmail.trim() || customPhone.trim())
            ? { email: customEmail.trim() || undefined, phone: customPhone.trim() || undefined }
            : undefined,
      },
      notes: notesArray.length > 0 ? notesArray : undefined,
      date: toIsoDate(date) || new Date().toISOString(),
      task_id: selectedTaskId || taskId,
    };

    if (invoice?.id) {
      const updatedInvoice: Invoice = {
        ...invoice,
        ...payload,
        amount: {
          number: String(payload.amount.number),
          currency: payload.amount.currency,
        },
        // document_id: isDocumentRemoved ? null : documentId,
      };
      await updateInvoice(String(invoice.id), updatedInvoice);
    } else {
      await createInvoice({
        ...payload,
        needs_approval: isOwner ? undefined : needApproval,
      });
    }
    handleClose();
    onSuccess?.();
  };

  return (
    <>
      <ModalOrPanel
        asPanel={asPanel}
        open={open && !isDeleteConfirmOpen && !isTemporarilyClosed}
        onClose={handleClose}
        title={
          isEditing
            ? 'Edit Invoice'
            : invoice
              ? `Invoice #${invoice.invoice_number}`
              : 'Add Invoice'
        }
        badge={
          asPanel && invoice && !isEditing ? (
            <StatusPill label={invoiceStatusLabel(invoice)} tone={invoiceStatusTone(invoice)} />
          ) : undefined
        }
        footer={
          asPanel && invoice && !isEditing ? (
            <>
              <ButtonOutlined type="button" onClick={handleEditClick}>
                Edit
              </ButtonOutlined>
              {canPay && !isInvoicePaid(invoice) && (
                <Button disabled={payingId === invoice.id} onClick={() => void payInvoice(invoice)}>
                  {payingId === invoice.id ? 'Paying…' : 'Pay Invoice'}
                </Button>
              )}
            </>
          ) : undefined
        }
        haveActions={hasExistingInvoice && !isEditing}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
      >
        {showForm ? (
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.uploadWrapper}>
              <div className={styles.uploadInfo}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf,application/pdf"
                  className={styles.uploadInput}
                  onChange={handleFileChange}
                />
                {documentFileName ? (
                  <></>
                ) : (
                  <span className={styles.uploadLabel} onClick={handleUploadClick}>
                    Upload the document to extract invoice info with AI
                  </span>
                )}
                {documentFileName && <span className={styles.uploadName}>{documentFileName}</span>}
              </div>
              <div className={styles.uploadActions}>
                {extractFile && (
                  <button
                    type="button"
                    className={styles.extractButton}
                    onClick={handleExtract}
                    disabled={isExtracting}
                  >
                    {isExtracting ? (
                      <LoaderSpinner size={24} fullHeight={false} label="" />
                    ) : (
                      'Extract'
                    )}
                  </button>
                )}
                {documentId && (
                  <button
                    type="button"
                    className={styles.uploadButton}
                    onClick={(event) => {
                      event.stopPropagation();
                      setDocumentId('');
                      setDocumentFileName('');
                      setExtractFile(null);
                      // setIsDocumentRemoved(true);
                    }}
                  >
                    <CgFileRemove width={32} height={32} color={'var(--color-cold-white)'} />
                  </button>
                )}
                {!documentId && (
                  <button onClick={handleUploadClick} type="button" className={styles.uploadButton}>
                    {isUploading ? (
                      <LoaderSpinner size={24} fullHeight={false} label="" />
                    ) : (
                      <IoDocumentTextOutline
                        width={32}
                        height={32}
                        color={'var(--color-cold-white)'}
                      />
                    )}
                  </button>
                )}
              </div>
            </div>

            <Input
              label="Number"
              type="text"
              placeholder="Invoice number"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
            />

            <div className={styles.dateRow}>
              <DateTimePicker
                label="Date"
                value={date ? date.toISOString() : null}
                onChange={(value) => {
                  const nextDate = value ? new Date(value) : null;
                  setDate(nextDate);
                  if (dueDate && nextDate && nextDate >= dueDate) {
                    setDueDate(addDays(nextDate, 1));
                  }
                }}
                maxDate={maxDate}
                placeholder="Choose the date"
                showTimeInput={false}
                className={styles.invoiceDatePick}
              />
              <DateTimePicker
                label="Due Date (Optional)"
                value={dueDate ? dueDate.toISOString() : null}
                onChange={(value) => {
                  const nextDueDate = value ? new Date(value) : null;
                  setDueDate(nextDueDate);
                  if (date && nextDueDate && date >= nextDueDate) {
                    setDate(addDays(nextDueDate, -1));
                  }
                }}
                minDate={minDueDate}
                placeholder="Choose the date"
                showTimeInput={false}
              />
            </div>

            <div className={styles.selectWrapper}>
              <Autocomplete
                label="Recipient"
                placeholder={
                  canChooseRecipient
                    ? 'Search service provider or enter a name'
                    : 'Search service provider'
                }
                value={merchantSearch}
                onChange={(value) => {
                  setMerchantSearch(value);
                  if (merchantError) setMerchantError('');
                }}
                options={serviceProviders.map(({ id, name }) => ({ label: name, value: id }))}
                onItemSelect={(id) => {
                  setMerchantId((id as string) ?? '');
                  setCustomName('');
                  setCustomEmail('');
                  setCustomPhone('');
                  setCounterpartHint(undefined);
                  if (id) {
                    setMerchantSearch('');
                    if (merchantError) setMerchantError('');
                  }
                }}
                // A name that is not one of the providers is taken as is — the recipient of
                // a one-off invoice, saved as the invoice's company name and nowhere else.
                onNewOption={
                  canChooseRecipient
                    ? (typed) => {
                        const name = typed.trim();
                        if (!name) return;
                        setMerchantId('');
                        setCustomName(name.slice(0, 500));
                        setCounterpartHint(undefined);
                        setMerchantSearch('');
                        if (merchantError) setMerchantError('');
                      }
                    : undefined
                }
                newOptionLabel={(typed) => `Use "${typed}"`}
                selectedOptionLabel={merchant?.name ?? (customName || counterpartHint)}
                mandatory
                error={merchantError}
              />
            </div>

            {canChooseRecipient && !merchant && customName && (
              <div className={styles.row}>
                <Input
                  label="Email (Optional)"
                  type="email"
                  placeholder="name@example.com"
                  value={customEmail}
                  onChange={(e) => setCustomEmail(e.target.value)}
                />
                <Input
                  label="Phone (Optional)"
                  type="tel"
                  placeholder="+1 555 000 0000"
                  value={customPhone}
                  onChange={(e) => setCustomPhone(e.target.value)}
                />
              </div>
            )}

            <div className={styles.selectWrapper}>
              <Autocomplete
                label="Add Task"
                placeholder="Search task"
                value={taskSearch}
                onChange={setTaskSearch}
                options={taskOptions
                  .filter((task) => task.title.toLowerCase().includes(taskSearch.toLowerCase()))
                  .map((task) => ({ label: task.title, value: task.id }))}
                onItemSelect={(id) => {
                  setSelectedTaskId((id as string) ?? '');
                  if (id) {
                    setTaskSearch('');
                  }
                }}
                selectedOptionLabel={taskOptions.find((t) => t.id === selectedTaskId)?.title}
              />
            </div>

            <div className={styles.selectWrapper}>
              <Autocomplete
                label="Category"
                placeholder="Search category"
                value={categorySearch}
                onChange={setCategorySearch}
                options={transactionCategories
                  .filter(({ title }) => title.toLowerCase().includes(categorySearch.toLowerCase()))
                  .map(({ id, title }) => ({ label: title, value: id }))}
                onItemSelect={(id) => {
                  setCategoryId((id as string) ?? '');
                  if (id) {
                    setCategorySearch('');
                  }
                }}
                selectedOptionLabel={
                  transactionCategories.find(({ id }) => id === categoryId)?.title
                }
              />
            </div>

            <div className={styles.row}>
              <Input
                label="Total Amount"
                placeholder="0.00"
                suffix="$"
                {...moneyInputProps}
                value={formatAmountDisplay(amount)}
                onChange={(e) => setAmount(sanitizeAmount(e.target.value))}
              />
            </div>

            <div className={styles.selectWrapper}>
              <ExpenseEquipmentField value={equipmentId} onChange={setEquipmentId} enabled={open} />
            </div>

            <TextArea
              label="Description"
              placeholder="You can add description here"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />

            {!invoice && !isOwner && (
              <Checkbox
                id="invoice-need-approval"
                label="Needs owner approval"
                checked={needApproval}
                onChange={(event) => setNeedApproval(event.target.checked)}
              />
            )}

            <div className={styles.buttons}>
              <ButtonOutlined type="button" onClick={handleClose}>
                Cancel
              </ButtonOutlined>
              <Button type="submit" disabled={loading || isUploading || isExtracting}>
                {isUploading
                  ? 'Uploading...'
                  : isExtracting
                    ? 'Extracting...'
                    : loading
                      ? 'Saving...'
                      : 'Save'}
              </Button>
            </div>
          </form>
        ) : (
          <ExistingInvoice
            invoice={invoice}
            tasks={taskOptions}
            equipmentList={equipmentList}
            documentName={documentName}
            counterpartName={counterpartName}
            onPreviewDocument={handleOpenPreview}
          />
        )}
      </ModalOrPanel>
      {previewDocument && (
        <DocumentPreviewModal document={previewDocument} onClose={handleClosePreview} />
      )}
      <ConfirmationModal
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Invoice"
        message="Are you sure you want to delete this invoice?"
      />
    </>
  );
};

const ExistingInvoice = ({
  invoice,
  tasks,
  equipmentList,
  documentName,
  counterpartName,
  onPreviewDocument,
}: ExistingInvoiceProps) => {
  const { data: invoiceDocument } = useDocumentByIdQuery(invoice?.document_id);
  const transactionCategories = useTransactionsStore((state) => state.transactionCategories);
  // One provider, by the id the invoice carries — the read-only view needs a name, not
  // a roster to search through.
  const serviceProvider = useSingleServiceProviderQuery({
    id: invoice?.service_provider_id ?? '',
    vesselId: getServiceId(),
  }).data;

  const toLocalDate = (value?: string | Date | null) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString('en-US');
  };

  const serviceProviderName =
    serviceProvider?.name ??
    counterpartName ??
    invoice?.company_name ??
    invoice?.service_provider_id ??
    '-';
  const recipientContact = formatRecipientContact(invoiceRecipientContact(invoice));

  const taskName = tasks.find((t) => t.id === invoice?.task_id)?.title ?? invoice?.task_id ?? '-';

  const categoryId = (invoice?.detail as { category?: string })?.category;
  const categoryName =
    transactionCategories.find((c) => c.id === categoryId || c.slug === categoryId)?.title ?? '-';

  const equipmentName =
    invoice?.equipments?.[0]?.name ??
    equipmentList.find((e) => e.id === invoice?.equipments?.[0]?.id)?.name ??
    '-';

  const description = (invoice?.notes ?? [])
    .map((n) => n.note)
    .filter(Boolean)
    .join('\n');

  const handleDocumentClick = () => {
    if (invoiceDocument) {
      onPreviewDocument({
        contentType: invoiceDocument.contentType,
        name: invoiceDocument.name,
        storageLink: invoiceDocument.storageLink,
      });
    }
  };

  return (
    <div className={viewStyles.existingTransactions}>
      <div className={viewStyles.heading}>
        <p className={viewStyles.date}>{toLocalDate(invoice?.date)}</p>
        <p className={viewStyles.amount}>
          {invoice?.amount?.number} {invoice?.amount?.currency}
        </p>
      </div>
      <div className={viewStyles.recipientEquipment}>
        <div>
          <span>Recipient</span>
          <span>{serviceProviderName}</span>
          <Divider className={viewStyles.modalDivider} />
        </div>
        <div>
          <span>Category</span>
          <span>{categoryName}</span>
          <Divider className={viewStyles.modalDivider} />
        </div>
      </div>
      {recipientContact && (
        <div className={viewStyles.fieldGroup}>
          <span>Recipient contact</span>
          <span>{recipientContact}</span>
          <Divider className={viewStyles.modalDivider} />
        </div>
      )}
      <div className={viewStyles.typeMethod}>
        <div>
          <span>Task</span>
          <span>{taskName}</span>
          <Divider className={viewStyles.modalDivider} />
        </div>
        <div>
          <span>Invoice Number</span>
          <span>{invoice?.invoice_number ?? '-'}</span>
          <Divider className={viewStyles.modalDivider} />
        </div>
      </div>
      {equipmentName && (
        <div className={viewStyles.fieldGroup}>
          <span>Equipment</span>
          <span>{equipmentName}</span>
          <Divider className={viewStyles.modalDivider} />
        </div>
      )}
      {description && (
        <div className={viewStyles.fieldGroup}>
          <span>Description</span>
          <span>{description}</span>
          <Divider className={viewStyles.modalDivider} />
        </div>
      )}
      {invoice?.document_id && (
        <div
          className={viewStyles.invoiceCard}
          onClick={handleDocumentClick}
          style={{ cursor: invoiceDocument ? 'pointer' : 'default' }}
        >
          <div>
            <p>{invoiceDocument?.name ?? documentName ?? 'Document'}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceModal;
